import React from "react";
import { SimpleGrid, Text, Box, Flex, type IconProps, Icon, type FlexProps } from "@chakra-ui/react";
import { Checklist, ChecklistItem } from "ui/checklist";
import { BookDemoButton, trackLoginClicked } from "components/event-tracking";
import { Button } from "ui/button";
import { Link } from "ui/link";

export type PricingCardProps = {
  id: "ai-decisioning" | "composable-cdp" | "reverse-etl";
  /** @default column */
  direction?: "row" | "column";
  heading: string;
  description: string;
  cta: {
    variant: "demo" | "login";
    subtext?: string;
  };
  detailsHeading?: string;
  details?: Array<{
    icon: (props: IconProps) => JSX.Element;
    title: string;
    href: string;
  }>;
  included: string[];
};

export function PricingCard({
  direction = "column",
  heading,
  description,
  cta,
  detailsHeading,
  details,
  included,
  ...flexProps
}: PricingCardProps & Pick<FlexProps, "order">) {
  return (
    <Flex
      align="start"
      bg="white"
      borderRadius={{ base: "smaller", sm: "small", md: "bigger" }}
      border="1px"
      borderColor="brand.border.extraLight"
      boxShadow="md"
      flexDir={{ base: "column", lg: direction }}
      fontSize="brand.sm"
      gap={{ base: "bigger", lg: direction === "column" ? "bigger" : "huge" }}
      flex={1}
      w="100%"
      p={{ base: "small", sm: "big", md: "bigger", lg: "48px" }}
      pos="relative"
      {...flexProps}
    >
      <Box>
        <Text as="h2" textStyle="headline2">
          {heading}
        </Text>

        <Text
          color="brand.textSecondary.light"
          textStyle="caption1"
          mt="smaller"
          w="100%"
          maxW={{ base: "100%", md: "52ch" }}
        >
          {description}
        </Text>

        <Flex mt="small">
          {cta.variant === "demo" ? (
            <BookDemoButton />
          ) : (
            // TODO: Shared login button would be nice
            <Button href="https://app.hightouch.com/login" variant="secondary" onClick={trackLoginClicked}>
              Get started
            </Button>
          )}
        </Flex>

        {cta.subtext && (
          <Text textStyle="caption1" mt="smaller" ml="tinier" color="brand.textSecondary.light" maxW="48ch">
            {cta.subtext}
          </Text>
        )}

        {detailsHeading && (
          <Text mt="bigger" textStyle="headline3">
            {detailsHeading}
          </Text>
        )}

        {details && (
          <SimpleGrid
            columns={{ base: 1, md: details.length > 3 ? 2 : 1 }}
            gap="smaller"
            mt="smaller"
            w="100%"
            maxW="600px"
          >
            {details?.map((detail) => (
              <ProductLink key={detail.title} {...detail} />
            ))}
          </SimpleGrid>
        )}
      </Box>

      <Box>
        <Text textStyle="headline3">Included</Text>

        <Checklist mt="smaller">
          {included.map((listItem) => (
            <ChecklistItem key={listItem}>{listItem}</ChecklistItem>
          ))}
        </Checklist>
      </Box>
    </Flex>
  );
}

type LinkProps = {
  title: string;
  href: string;
  icon: (props: IconProps) => JSX.Element;
};

export function ProductLink({ title, href, icon }: LinkProps) {
  return (
    <Link
      aria-label={title}
      width="100%"
      href={href}
      className="group"
      textStyle="caption1"
      fontWeight="normal"
      textDecoration="none"
      flexDir="column"
      alignItems="start"
      _hover={{ textDecoration: "none" }}
    >
      <Flex align="center" gap={2} transition="all .15s ease" _groupHover={{ color: "new.green.700" }}>
        <Icon as={icon} boxSize="icon.md" />

        <Text as="span" display="flex" textStyle="caption1">
          {title}
        </Text>
      </Flex>
    </Link>
  );
}
